import React from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { FaCheck } from 'react-icons/fa6';
import { SubscriptionType, BusinessSubscriptionType, AnySubscriptionType } from '../../../types/misc';
import { baseSubscriptionPrices, subscriptionFeatures, variableSubscriptionPrices } from '../../../utils/subscriptionUtils';
import { businessSubscriptionConfig, businessBaseSubscriptionPrices, businessVariableSubscriptionPrices, businessSubscriptionFeatures, communitySubscriptionPrices } from '../../../utils/BusinessFeatureUtils';

export type SubscriptionDisplayMode = 'cards' | 'selectable';

export interface SubscriptionSelectorProps {
  selectedSubscription?: AnySubscriptionType | null;
  onSubscriptionSelect?: (subscriptionType: AnySubscriptionType) => void;
  mode: SubscriptionDisplayMode;
  showTrialPeriod?: boolean;
  trialDays?: number;
  showActionButtons?: boolean;
  selectedActionText?: string;
  unselectedActionText?: string;
  onActionClick?: (subscriptionType: AnySubscriptionType) => void;
  className?: string;
  isBusiness?: boolean;
  isSubCommunity?: boolean;
}

export function SubscriptionSelector({
  selectedSubscription,
  onSubscriptionSelect,
  mode,
  showTrialPeriod = false,
  trialDays = 30,
  showActionButtons = false,
  selectedActionText,
  unselectedActionText,
  onActionClick,
  className,
  isBusiness = false,
  isSubCommunity = false,
}: SubscriptionSelectorProps) {
  const { t } = useTranslation('', { keyPrefix: 'page.onboarding.subscription' });
  const { t: tSettings } = useTranslation('', { keyPrefix: 'page.subscriptions' });
  
  const subscriptionOptions: AnySubscriptionType[] = isBusiness
    ? [BusinessSubscriptionType.STARTER, BusinessSubscriptionType.PROFESSIONAL]
    : [SubscriptionType.FREE, SubscriptionType.BASE, SubscriptionType.PRO];

  const isComingSoon = (sub: AnySubscriptionType) =>
    isBusiness
      ? !!businessSubscriptionConfig[sub as BusinessSubscriptionType]?.isComingSoon
      : sub === SubscriptionType.PRO;

  const isMostChosen = (sub: AnySubscriptionType) =>
    isBusiness
      ? sub === BusinessSubscriptionType.PROFESSIONAL
      : sub === SubscriptionType.BASE;

  const getPrice = (sub: AnySubscriptionType) =>
    isBusiness
      ? businessBaseSubscriptionPrices[sub as BusinessSubscriptionType]
      : isSubCommunity
        ? communitySubscriptionPrices[sub as SubscriptionType]
        : baseSubscriptionPrices[sub as SubscriptionType];

  const getVariablePrice = (sub: AnySubscriptionType) =>
    isBusiness
      ? businessVariableSubscriptionPrices[sub as BusinessSubscriptionType]
      : variableSubscriptionPrices[sub as SubscriptionType];

  const getPrevTier = (sub: AnySubscriptionType): AnySubscriptionType | null => {
    if (isBusiness) {
      return sub === BusinessSubscriptionType.PROFESSIONAL ? BusinessSubscriptionType.STARTER : null;
    }
    if (sub === SubscriptionType.BASE) return SubscriptionType.FREE;
    if (sub === SubscriptionType.PRO)  return SubscriptionType.BASE;
    return null;
  };

  const featureList = isBusiness ? businessSubscriptionFeatures : subscriptionFeatures;

  const getCardClassName = (sub: AnySubscriptionType) => {
    const baseClasses = 'relative rounded-xl border-2 p-6';
    const coming = isComingSoon(sub);

    if (mode === 'cards') {
      return classNames(baseClasses, 'flex flex-col h-full', {
        'opacity-50': coming,
        'border-primary-900 bg-opacity-10 shadow-md': selectedSubscription === sub,
        'border-secondary-200 bg-secondary-200 bg-opacity-30': selectedSubscription !== sub && !coming,
        'border-gray-500 bg-gray-300': coming,
      });
    }

    return classNames(baseClasses, 'group transition-all duration-300', {
      'cursor-pointer hover:shadow-lg': !coming,
      'cursor-not-allowed opacity-50': coming,
      'border-primary-900 bg-opacity-10 shadow-md': selectedSubscription === sub,
      'border-secondary-200 bg-secondary-200 bg-opacity-30 hover:border-primary-900 hover:bg-opacity-40': selectedSubscription !== sub && !coming,
      'border-gray-500 bg-gray-300': coming,
    });
  };

  const renderTopBadge = (sub: AnySubscriptionType) => {
    if (mode === 'cards' && selectedSubscription === sub) {
      return (
        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
          <div className="bg-primary-900 text-secondary-200 px-3 py-1 rounded-full text-xs font-bold">
            {tSettings('activeSubscription')}
          </div>
        </div>
      );
    }

    if (isMostChosen(sub)) {
      return (
        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
          <div className="bg-primary-900 text-secondary-200 px-3 py-1 rounded-full text-xs font-bold">
            {t('mostChosen')}
          </div>
        </div>
      );
    }

    if (isComingSoon(sub)) {
      return (
        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
          <div className="bg-gray-400 text-gray-900 px-3 py-1 rounded-full text-xs font-bold">
            {t('comingSoon')}
          </div>
        </div>
      );
    }

    return null;
  };

  const renderSelectionIndicator = (sub: AnySubscriptionType) => {
    if (mode !== 'selectable') return null;
    const coming = isComingSoon(sub);

    return (
      <div
        className={classNames(
          'absolute right-3 top-3 flex h-6 w-6 items-center justify-center rounded-full border-2 transition-all duration-200',
          {
            'border-primary-900 bg-primary-900': selectedSubscription === sub,
            'border-secondary-200 group-hover:border-primary-300': selectedSubscription !== sub && !coming,
            'border-gray-500 bg-gray-400': coming,
          }
        )}>
        {selectedSubscription === sub && (
          <FaCheck className="h-3 w-3 text-secondary-50" />
        )}
      </div>
    );
  };

  const renderPricing = (sub: AnySubscriptionType) => {
    const coming = isComingSoon(sub);
    const hasTrial = sub === SubscriptionType.BASE 
    || sub === BusinessSubscriptionType.STARTER
    || sub === SubscriptionType.PRO
    || sub === BusinessSubscriptionType.PROFESSIONAL;
    const price = getPrice(sub);

    if (showTrialPeriod && hasTrial) {
      return (
        <div className="flex items-baseline gap-1">
          <div className="flex flex-col items-start">
            <span className="text-2xl font-bold text-primary-900">
              {t('freePeriod', { days: trialDays.toString() })}
            </span>
            <span className="text-xs opacity-75 text-primary-900">
              {`${t('afterFreePeriod')} €${price?.toFixed(2) || '0.00'}/${t('perMonth')}`}
            </span>
          </div>
        </div>
      );
    }

    return (
      <div className="flex items-baseline gap-1">
        <span className={classNames('text-2xl font-bold', {
          'text-primary-900': !coming,
          'text-gray-900':    coming,
        })}>
          €{price?.toFixed(2) || '0.00'}
        </span>
        <span className={classNames('text-sm opacity-75', {
          'text-primary-900': !coming,
          'text-gray-900':    coming,
        })}>
          /{t('perMonth')}
        </span>
      </div>
    );
  };

  const renderActionButton = (sub: AnySubscriptionType) => {
    if (!showActionButtons || isComingSoon(sub) || !buttonText(sub) || !onActionClick) return null;

    return (
      <div className="mt-6">
        <button
          onClick={() => onActionClick(sub)}
          type="button"
          className="w-full rounded-lg border-2 px-4 py-2 text-center text-sm font-bold transition-all border-primary-900 bg-primary-900 hover:bg-primary-100 text-secondary-200 hover:bg-primary-400">
          {buttonText(sub)}
        </button>
      </div>
    );
  };

  const buttonText = (sub: AnySubscriptionType) =>
    selectedSubscription === sub ? selectedActionText : unselectedActionText;

  const handleCardClick = (sub: AnySubscriptionType) => {
    if (mode === 'selectable' && !isComingSoon(sub) && onSubscriptionSelect) {
      onSubscriptionSelect(sub);
    }
  };

  return (
    <div className={classNames('grid grid-cols-1 gap-6', {
      'md:grid-cols-2 max-w-4xl mx-auto': isBusiness,
      'md:grid-cols-3': !isBusiness,
    }, className)}>
      {subscriptionOptions.map((sub) => {
        const coming = isComingSoon(sub);
        const prevTier = getPrevTier(sub);
        const varPrice = getVariablePrice(sub);

        return (
          <div
            key={sub}
            onClick={() => handleCardClick(sub)}
            className={classNames(getCardClassName(sub), 'w-full max-w-md')}>

            {renderTopBadge(sub)}
            {renderSelectionIndicator(sub)}

            <div className={mode === 'cards' ? 'flex-1 flex flex-col' : ''}>
              <div className="mb-4">
                <h3 className={classNames('text-xl font-bold mb-2', {
                  'text-primary-900': !coming,
                  'text-gray-900':    coming,
                })}>
                  {t(`${sub.toLowerCase()}.title`)}
                </h3>
                <p className={classNames('text-sm opacity-75 mb-3', {
                  'text-primary-900': !coming,
                  'text-gray-900':    coming,
                })}>
                  {t(`${sub.toLowerCase()}.subtitle`)}
                </p>

                {renderPricing(sub)}

                {/* Variable pricing note - sub-communities use flat fees only */}
                {!isSubCommunity && varPrice > 0 && (
                  <div className="mt-1">
                    <span className={classNames('text-xs opacity-75', {
                      'text-primary-900': !coming,
                      'text-gray-900':    coming,
                    })}>
                      {tSettings('priceNote', { price: varPrice.toFixed(2) })}
                    </span>
                  </div>
                )}
              </div>

              <div>
                <h4 className={classNames('font-semibold text-sm', {
                  'text-primary-900': !coming,
                  'text-gray-900':    coming,
                })}>
                  {t('features')}:
                </h4>
                <div className="space-y-1">
                  {/* "Everything in <prev tier>…" label */}
                  {prevTier && (
                    <div className="pb-2">
                      <span className={classNames('text-xs', {
                        'text-primary-900': !coming,
                        'text-gray-900':    coming,
                      })}>
                        {t(`everythingIn${prevTier.charAt(0).toUpperCase() + prevTier.slice(1).toLowerCase()}`)}
                      </span>
                    </div>
                  )}
                  {/* Features exclusive to this tier */}
                  {(featureList as any[])
                    .filter((feature: any) =>
                      feature[sub] && (prevTier === null || !feature[prevTier])
                    )
                    .map((feature: any) => (
                      <div key={feature.i18nKey} className="flex items-center gap-2">
                        <div className={classNames('flex h-4 w-4 items-center justify-center rounded-full', {
                          'bg-primary-900': !coming,
                          'bg-gray-500':    coming,
                        })}>
                          <FaCheck className={classNames('h-2 w-2', {
                            'text-secondary-200': !coming,
                            'text-gray-900':      coming,
                          })} />
                        </div>
                        <span className={classNames('text-xs', {
                          'text-primary-900': !coming,
                          'text-gray-900':    coming,
                        })}>
                          {tSettings(feature.i18nKey)}
                          {feature.isComingSoon && (
                            <span className="ml-1 rounded-md bg-primary-900 px-2 py-0.5 text-sm text-secondary-50">
                              {t('comingSoon')}
                            </span>
                          )}
                        </span>
                      </div>
                    ))}
                </div>
              </div>
            </div>

            {renderActionButton(sub)}
          </div>
        );
      })}
    </div>
  );
}
